import useSWR from "swr";
import { find } from "lodash";
import { AICC_LANGUAGE_STATUS } from "constants/aiccLanguage";
import { AICC_SANDBOX_BASE_SCOPE, AiccSandboxBaseScope } from "constants/aiccPromptSandbox";
import { aiccLessonReviewScriptApi } from "services/aiccLearningAssets";
import { AICC_SWR_CONFIG } from "./swrConfig";

/**
 * The lesson scripts this language has laid down, for the sandbox's own script picker.
 *
 * 🔴 THE SAME ENDPOINT THE LEARNING ASSETS PAGE READS, and deliberately not a new one: what the operator picks here
 * has to be what they can go and look at there. It answers with every status group at once, which is why the key
 * carries no scope — the choice between them is made below, not by a second request.
 *
 * 🔴 THE SCOPE IS MATCHED, NOT IGNORED. `base_scope` governs the SCRIPT as well as the prompts (§4.7.1), so a picker
 * showing the published scripts beside a run reading the draft ones would have an operator choosing a conversation
 * the turn then does not walk. The backend re-reads it by scope regardless; this list only has to agree with it.
 */
export function useAiccLessonReviewScripts(languageId: number | undefined, baseScope: AiccSandboxBaseScope) {
  const { data, error, isLoading } = useSWR(
    languageId ? ["aicc/lesson-review-scripts", languageId] : null,
    () => aiccLessonReviewScriptApi.fetchVersions(languageId as number),
    AICC_SWR_CONFIG
  );

  const versions = data?.versions ?? [];
  const published = find(versions, ({ status }) => status === AICC_LANGUAGE_STATUS.public);
  // Whatever is being worked on: the asset pages carry `draft` and `preview-ready`, and either is "not published".
  const working = find(versions, ({ status }) => status !== AICC_LANGUAGE_STATUS.public);
  const group = baseScope === AICC_SANDBOX_BASE_SCOPE.live ? published ?? working : working ?? published;

  return {
    lessons: group?.items ?? [],
    /** Which group the list came from, so the panel can say so rather than implying the scope was honoured. */
    scriptStatus: group?.status,
    isLoading,
    isError: !!error,
  };
}
